import { useNavigation } from "@repo/core/hooks/useNavigation";
import { useDocumentCaptureStore } from "@repo/core/stores/documentCapture";
import { useState, useEffect } from "react";
import { usePreProposal } from "../../hooks/usePreProposal";
import { useUploadProposalDocument } from "@repo/core/hooks/useUploadProposalDocument";
import useToast from "@repo/core/components/core/Toast";
import { getApiErrorMessage } from "../../services/getApiErrorMessage.service";

type PermissionState = "granted" | "denied" | "prompt" | "error" | null;

export const CaptureDocumentController = () => {
  const { back, params } = useNavigation();
  const {
    selectDocument,
    currentDocumentCapture,
    setCameraFacing: setStoredCameraFacing
  } = useDocumentCaptureStore();
  const initialCameraFacing =
    (params?.cameraFacing as "front" | "back") || "back";
  const [cameraFacing, setCameraFacing] = useState<"front" | "back">(
    initialCameraFacing
  );
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isCheckingPermissions, setIsCheckingPermissions] = useState(true);
  const [cameraPermission, setCameraPermission] =
    useState<PermissionState>(null);
  const { proposalData } = usePreProposal();
  const { uploadDocumentFromBase64 } = useUploadProposalDocument();
  const toast = useToast();

  const onCaptureImage = async (base64: string) => {
    setCapturedImage(base64);
  };

  const onSaveImage = async () => {
    if (!capturedImage || !currentDocumentCapture) return;

    setIsUploading(true);

    try {
      // Store image in documentCaptureStore
      selectDocument(capturedImage);

      // Upload immediately
      if (!proposalData?.lngProposta) {
        toast.error("Proposta não encontrada. Tente novamente.");
        setIsUploading(false);
        return;
      }

      await uploadDocumentFromBase64(proposalData.lngProposta, capturedImage, {
        strTipoDocumento: currentDocumentCapture,
        strObservacoes: `Documento capturado manualmente - ${currentDocumentCapture}`,
        strCliente: proposalData.strCliente || "",
        strCpfPortador: proposalData.strCliente || "",
        strAutorizacao: "S",
        datDocumentoExterno: new Date().toISOString(),
        strVisualizarWEB: "S"
      });

      toast.success("Documento enviado com sucesso!");
      back();
    } catch (error) {
      console.error("Error uploading captured image:", error);
      toast.error(
        getApiErrorMessage(error) ||
          "Erro ao enviar documento. Tente novamente."
      );
      setIsUploading(false);
    }
  };

  const onChangeImage = () => {
    setCapturedImage(null);
  };

  const onCancelCapture = () => {
    back();
  };

  const onToggleCameraFacing = () => {
    setCapturedImage(null);
    setCameraFacing((currentFacing) => {
      const nextFacing = currentFacing === "front" ? "back" : "front";
      setStoredCameraFacing(nextFacing);
      return nextFacing;
    });
  };

  const requestCameraPermission = async () => {
    try {
      // First, try to query permission status
      const permissionStatus = await navigator.permissions.query({
        name: "camera" as PermissionName
      });

      if (
        permissionStatus.state === "denied" ||
        permissionStatus.state === "prompt"
      ) {
        // Request camera access
        const permissionStream = await navigator.mediaDevices.getUserMedia({
          video: true
        });
        permissionStream.getTracks().forEach((track) => track.stop());
        setCameraPermission("granted");
        toast.success("Permissão para acessar a câmera foi concedida!");
      } else {
        setCameraPermission(permissionStatus.state as PermissionState);
      }
    } catch (error) {
      console.error("Error requesting camera permission:", error);
      setCameraPermission("denied");
      toast.error(
        "Permissão para acessar a câmera foi negada. Por favor, habilite-a nas configurações do seu navegador."
      );
    }
  };

  const handleRequestPermission = async () => {
    await requestCameraPermission();
  };

  // Check camera permissions on mount
  useEffect(() => {
    const checkPermissions = async () => {
      try {
        setIsCheckingPermissions(true);

        // Check if browser supports camera access
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          console.error("Camera not supported in this browser");
          setCameraPermission("error");
          toast.error("Câmera não suportada em seu navegador.");
          return;
        }

        // Try to check permission status
        try {
          const permissionStatus = await navigator.permissions.query({
            name: "camera" as PermissionName
          });

          if (permissionStatus.state === "granted") {
            setCameraPermission("granted");
          } else if (permissionStatus.state === "denied") {
            setCameraPermission("denied");
            toast.error(
              "Permissão para acessar a câmera foi negada. Por favor, habilite-a nas configurações do seu navegador para capturar documentos."
            );
          } else {
            // Permission is in 'prompt' state, will ask when camera is accessed
            setCameraPermission("prompt");
          }
        } catch (error) {
          // If query is not supported, try to request directly
          console.log(
            "Permission query not supported, will request on camera access"
          );
          setCameraPermission("prompt");
        }
      } catch (error) {
        console.error("Error checking camera permissions:", error);
        setCameraPermission("error");
        toast.error("Erro ao verificar permissões da câmera. Tente novamente.");
      } finally {
        setIsCheckingPermissions(false);
      }
    };

    checkPermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Web permission structure compatible with mobile
  const permission = cameraPermission
    ? {
        granted: cameraPermission === "granted",
        canAskAgain:
          cameraPermission === "prompt" || cameraPermission === "error"
      }
    : null;

  return {
    onCaptureImage,
    cameraFacing,
    capturedImage,
    onSaveImage,
    onChangeImage,
    onCancelCapture,
    onToggleCameraFacing: undefined,
    isUploading,
    currentDocumentCapture,
    permission,
    isCheckingPermissions,
    handleRequestPermission
  };
};
